define('presentation/signup/facades/signupPaymentMethodFacade',[
        'businessServices/authentication/stores/signupAuthenticationStore',
        'businessServices/creditCards/creditCardTypes',
        'businessServices/signup/signupSessionStore',
        'common/encryption/crypto',
        'common/promises/promiseFactory',
        'persistence/repositories/signupRepository',
        'presentation/signup/presentationObjects/signupPaymentMethodPresentationObject'
    ],
    function() {
        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        let _signupAuthenticationStore = null;
        let _signupRepository = null;
        let _signupSessionStore = null;

        const SignupPaymentMethodPresentationObjectConstructor = require('presentation/signup/presentationObjects/signupPaymentMethodPresentationObject');

        const CreditCardFormatterConstructor = require('businessServices/creditCards/creditCardTypes');
        const _creditCardFormatter = new CreditCardFormatterConstructor();

        const CryptoConstructor = require('common/encryption/crypto');
        const _crypto = new CryptoConstructor();

        const _isFraudulentPaymentInformation = (paymentMethod) => {
            const unformattedCardNumber = _creditCardFormatter.unformatCreditCard(paymentMethod.cardNumber);
            const firstSixDigits = unformattedCardNumber.slice(0,6);
            const cardNumberHashed = _crypto.createSha256Hash(paymentMethod.cardNumber);
            return _signupSessionStore.isFraudulentPaymentInformation(paymentMethod.cardHolderName,
                cardNumberHashed,
                firstSixDigits,
                paymentMethod.expirationDate,
                paymentMethod.countryCode,
                paymentMethod.postalCode);
        };

        const _saveBillingMethod = (billingMethod) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionStore.saveBillingMethod(billingMethod)
                    .fail(promise.reject)
                    .done(promise.resolve);
            });
        };

        const _saveAddressLineOne = (addressLineOne) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionStore.savePaymentMethodAddressLineOne(addressLineOne)
                    .fail(promise.reject)
                    .done(promise.resolve);
            });
        };

        const _saveAddressLineTwo = (addressLineTwo) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionStore.savePaymentMethodAddressLineTwo(addressLineTwo)
                    .fail(promise.reject)
                    .done(promise.resolve);
            });
        };

        const _saveCity = (city) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionStore.savePaymentMethodCity(city)
                    .fail(promise.reject)
                    .done(promise.resolve);
            });
        };

        const _saveStateOrProvince = (country, stateOrProvince) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionStore.savePaymentMethodRegion(country, stateOrProvince)
                    .fail(promise.reject)
                    .done(promise.resolve);
            });
        };

        const _savePostalCode = (country, postalCode) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionStore.savePaymentMethodPostalCode(country, postalCode)
                    .fail(promise.reject)
                    .done(promise.resolve);
            });
        };

        const _saveCardHolderName = (cardholderName) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionStore.savePaymentMethodHolderName(cardholderName)
                    .fail(promise.reject)
                    .done(promise.resolve);
            });
        };

        const _saveExpirationDate = (expirationMonth, expirationYear) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionStore.savePaymentMethodExpirationDate(expirationMonth, expirationYear)
                    .fail(promise.reject)
                    .done(promise.resolve);
            });
        };

        const _createAccount = (paymentMethod) => _promiseFactory.deferIndefinitely(deferredObject => {
            _signupAuthenticationStore.load()
                .fail(deferredObject.reject)
                .done((signupSessionId, accountNumber, secretKey) => {
                    _signupSessionStore.saveShouldBypassWelcomeEmail(paymentMethod.bypassWelcomeEmail)
                        .fail(deferredObject.reject)
                        .done(() => {
                            _signupSessionStore.getState()
                                .fail(deferredObject.reject)
                                .done(signupSession => {
                                    const signupData = {
                                        accountNumber: accountNumber,
                                        authorizedSignature: signupSession.authorizedSignature,
                                        billingMethod: signupSession.signupDocument.billingMethod,
                                        cardCvv: paymentMethod.cvvCode,
                                        cardExpirationDate: paymentMethod.expirationDate,
                                        cardHolderName: paymentMethod.cardHolderName,
                                        cardNumber: _creditCardFormatter.unformatCreditCard(paymentMethod.cardNumber),
                                        cardAddressLineOne: paymentMethod.addressLineOne,
                                        cardAddressLineTwo: paymentMethod.addressLineTwo,
                                        cardCity: paymentMethod.city,
                                        cardState: paymentMethod.stateProvince,
                                        cardCountryCode: paymentMethod.countryCode,
                                        cardPostalCode: paymentMethod.postalCode,
                                        cardType: paymentMethod.cardType,
                                        emailAddress: signupSession.signupDocument.emailAddress,
                                        password: signupSession.password,
                                        secretKey: secretKey,
                                        signupSessionId: signupSessionId,
                                        bypassPreAuthorization: paymentMethod.bypassPreAuthorization
                                    };

                                    _signupRepository.createAccount(signupData)
                                        .fail(deferredObject.reject)
                                        .done((response) => {
                                            if (!response) {
                                                deferredObject.reject(response);
                                            } else {
                                                deferredObject.resolve(response.status);
                                            }
                                        });
                                });
                        });
                });
        });

        const _loadSignupSession = () => {
            return _promiseFactory.defer((promise) => {
                _signupSessionStore.getState()
                    .fail(promise.reject)
                    .done((signupSession) => {
                        let signupPaymentMethodPresentationObject = new SignupPaymentMethodPresentationObjectConstructor();
                        signupPaymentMethodPresentationObject.billingMethod = signupSession.signupDocument.billingMethod;
                        signupPaymentMethodPresentationObject.cardHolderName = signupSession.signupDocument.paymentMethodHolderName;
                        signupPaymentMethodPresentationObject.expirationDate = signupSession.signupDocument.paymentMethodExpirationDate;
                        signupPaymentMethodPresentationObject.addressLineOne = signupSession.signupDocument.paymentMethodAddressLineOne;
                        signupPaymentMethodPresentationObject.addressLineTwo = signupSession.signupDocument.paymentMethodAddressLineTwo;
                        signupPaymentMethodPresentationObject.city = signupSession.signupDocument.paymentMethodCity;
                        signupPaymentMethodPresentationObject.state = signupSession.signupDocument.paymentMethodRegionCode;
                        signupPaymentMethodPresentationObject.country = signupSession.signupDocument.paymentMethodCountryCode;
                        signupPaymentMethodPresentationObject.postalCode = signupSession.signupDocument.paymentMethodPostalCode;
                        signupPaymentMethodPresentationObject.liveReceptionistPackageId = signupSession.signupDocument.liveReceptionistPackageId;
                        signupPaymentMethodPresentationObject.shouldBypassWelcomeEmail = signupSession.signupDocument.shouldBypassWelcomeEmail;
                        promise.resolve(signupPaymentMethodPresentationObject);
                    });
            });
        };

        const _init = () => {
            const SignupRepository = require('persistence/repositories/signupRepository');
            _signupRepository = new SignupRepository();
            _signupRepository.init();

            const SignupAuthenticationStoreConstructor = require('businessServices/authentication/stores/signupAuthenticationStore');
            _signupAuthenticationStore = new SignupAuthenticationStoreConstructor();
            _signupAuthenticationStore.init();

            const SignupSessionStoreConstructor = require('businessServices/signup/signupSessionStore');
            _signupSessionStore = new SignupSessionStoreConstructor();
            _signupSessionStore.init();
        };


        return function() {
            let self = this;

            self.init = _init;

            self.createAccount = _createAccount;
            self.isFraudulentPaymentInformation = _isFraudulentPaymentInformation;
            self.loadSignupSession = _loadSignupSession;
            self.saveBillingMethod = _saveBillingMethod;
            self.saveAddressLineOne = _saveAddressLineOne;
            self.saveAddressLineTwo = _saveAddressLineTwo;
            self.saveCardHolderName = _saveCardHolderName;
            self.saveCity = _saveCity;
            self.saveExpirationDate = _saveExpirationDate;
            self.saveStateOrProvince = _saveStateOrProvince;
            self.savePostalCode = _savePostalCode;
        };
    })
;

